import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SecondaryButton } from '@dev/ee-components';
import { AccessLevel, Campaign, DeliveryLogType } from '@dev/ee-api/ee-api-models';

import dayjs from '@/services/tools/dayjs';
import { ListFromCampaignParams } from '@/services/ElasticApi';
import { TIME_RANGE_35_DAYS } from '@/services/constants';
import { ApplicationState } from '@/store';
import { CreateListType } from '@/store/campaigns/types';
import { ShowDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { ListFromCampaignStart } from '@/store/lists/actions';

import { isFutureScheduledWithAnyDelivered } from '@/components/Campaigns/helperFunctions';
import { generateIdBasedOnPath } from '@dev/ee-tools';
import { useRouteMatch } from 'react-router';
import { pathsParams } from '@/services/dictionaries/Paths';
import { AddContactsToList } from '../../../../../Contacts/CommonActions/AddToList';
import { ContactsType } from '../../../../../Contacts/CommonActions/AddToList/AddToListProps';
const CanSeeExtendedLogs = (): boolean => {
    const { AccountAccessLevel } = useSelector((state: ApplicationState) => state.account.GeneralInfo);
    return AccountAccessLevel?.includes(AccessLevel.ExtendedLogs);
};

const IsContactsAvailable = (logDays: number, campaign: Campaign): boolean => {
    const sendDate = dayjs(campaign?.TriggerOptions?.DateToSendOn).utc(true);
    const currentDate = dayjs().utc();
    const calc = currentDate.diff(sendDate, 'day', true);
    return calc <= logDays;
};

export const CreateListButton = (props: CreateListType): JSX.Element => {
    const { t } = useTranslation();
    const { campaign, decodedName } = props;
    const dispatch = useDispatch();
    const { KeepLogsForDays } = useSelector((state: ApplicationState) => state.account.GeneralInfo);
    const selectedList = useRef({ list: '', newList: false });
    const selectedContactsType = useRef({ contactsType: ContactsType.Openers });
    const isExtendedLogs = CanSeeExtendedLogs();
    const logDays = isExtendedLogs ? TIME_RANGE_35_DAYS : KeepLogsForDays;

    const route = useRouteMatch<{ type: string }>();

    const routeForIds =
        route?.path?.replace?.(pathsParams.name, '')?.replace(pathsParams.type, route.params?.type) ?? '';

    const handleAddToList = (): void => {
        const params: ShowDialogParams = {
            title: t('addcontactsctolist'),
            confirmLabel: t('save'),
            onClickConfirm: (): void => {
                handleAddToListConfirm();
            },
            cancelLabel: t('cancel'),
            body: (
                <AddContactsToList
                    moveContacts={false}
                    onChange={(ev, selectedItems) => {
                        selectedList.current = { list: selectedItems.trim(), newList: ev };
                    }}
                    isContactsOptions={true}
                    onChangeContactsType={(contactsType) => {
                        selectedContactsType.current = { contactsType };
                    }}
                />
            ),
        };

        dispatch(ShowDialog(params));
    };

    const cannotCreateListModal = (): void => {
        const params: ShowDialogParams = {
            title: t('cannotcreatelistmodal_title'),
            cancelLabel: t('cancel'),
            body: <div>{t('cannotcreatelistmodal_desc', { days: logDays })}</div>,
        };
        dispatch(ShowDialog(params));
    };

    const handleAddToListConfirm = (): void => {
        const params: ListFromCampaignParams = {
            campaignName: decodedName,
            listName: selectedList.current.list,
            statuses: mapContactsType(selectedContactsType.current.contactsType),
            from: dayjs(campaign?.TriggerOptions?.DateToSendOn).utc(true).startOf('d').toJSON(),
            newList: selectedList.current.newList,
            lastEvent: true,
            ...(isFutureScheduledWithAnyDelivered(campaign) && {
                from: campaign?.DateAdded,
            }),
        };

        dispatch(ListFromCampaignStart(params));
    };

    const mapContactsType = (contactsType: ContactsType): DeliveryLogType[] => {
        switch (contactsType) {
            case ContactsType.Openers:
                return [DeliveryLogType.Clicked, DeliveryLogType.Opened];
            case ContactsType.Clickers:
                return [DeliveryLogType.Clicked];
            case ContactsType.Nonopeners:
                return [DeliveryLogType.Sent];
            case ContactsType.OpenedNotClicked:
                return [DeliveryLogType.Opened];
            default:
                return [];
        }
    };

    return (
        <SecondaryButton
            id={generateIdBasedOnPath('create-list-button', routeForIds)}
            text={t('createlist')}
            onClick={() => {
                IsContactsAvailable(logDays, campaign) ? handleAddToList() : cannotCreateListModal();
            }}
        />
    );
};
